import axios from 'axios';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Lista di endpoint pubblici che non richiedono autenticazione
const PUBLIC_ENDPOINTS = [
  '/settings',
  '/auth/login',
  '/auth/register'
];

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor per aggiungere il token alle richieste
api.interceptors.request.use((config) => {
  console.log('=== API Request Interceptor ===');
  console.log('Request URL:', config.url);
  console.log('Request Method:', config.method);
  
  // Verifica se l'endpoint è pubblico
  const isPublicEndpoint = PUBLIC_ENDPOINTS.some(endpoint => 
    config.url.endsWith(endpoint)
  );

  // Bypass token check in development
  if (import.meta.env.DEV) {
    config.headers.Authorization = 'Bearer dev-token';
    return config;
  }

  if (!isPublicEndpoint) {
    const token = localStorage.getItem('token');
    console.log('Token present:', !!token);
    console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
    
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        
        if (payload.exp * 1000 < Date.now()) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login?expired=true';
          return Promise.reject('Token expired');
        }
      } catch (error) {
        console.error('Token decode error:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login?invalid=true';
        return Promise.reject('Invalid token');
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      return Promise.reject({ response: { status: 401, data: { message: 'Token non fornito' } } });
    }
  }
  
  console.log('Final Headers:', config.headers);
  return config;
}, (error) => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => {
    console.log('=== API Response Success ===');
    console.log('Status:', response.status);
    console.log('Data preview:', JSON.stringify(response.data).substring(0, 100) + '...');
    return response;
  },
  (error) => {
    console.error('=== API Response Error ===');
    console.error('Status:', error.response?.status);
    console.error('Error:', error.response?.data);
    return Promise.reject(error);
  }
);

export const login = async (credentials) => {
  const response = await api.post('/auth/login', credentials);
  if (response.data.token) {
    localStorage.setItem('token', response.data.token);
  }
  return response.data;
};

export const register = async (userData) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

export const approveUser = async (userId) => {
  const response = await api.put(`/auth/approve/${userId}`);
  return response.data;
};

export const getPendingUsers = async () => {
  const response = await api.get('/auth/pending-users');
  return response.data;
};

export const getSettings = async () => {
  const response = await api.get('/settings');
  return response.data;
};

export const updateRegistrationSetting = async (enabled) => {
  const response = await api.put('/settings/registration', { enabled });
  return response.data;
};

export const getVolunteers = async () => {
  const response = await api.get('/users/volunteers');
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await api.put(`/users/${userId}`, userData);
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

export const getForms = () => api.get('/forms').then(res => res.data);

export const createForm = (formData) => api.post('/forms', formData).then(res => res.data);

export const toggleFormStatus = (formId) => api.put(`/forms/${formId}/toggle`).then(res => res.data);

export const updateForm = (formId, formData) => api.put(`/forms/${formId}`, formData).then(res => res.data);

export const getEvents = () => api.get('/events').then(res => res.data);

export const createEvent = (eventData) => api.post('/events', eventData).then(res => res.data);

export const updateEvent = (eventId, eventData) => api.put(`/events/${eventId}`, eventData).then(res => res.data);

export const deleteEvent = (eventId) => api.delete(`/events/${eventId}`).then(res => res.data);

export const joinEvent = (eventId) => api.post(`/events/${eventId}/join`).then(res => res.data);

export const leaveEvent = (eventId) => api.post(`/events/${eventId}/leave`).then(res => res.data);

export const removeEventParticipant = async (eventId, participantId) => {
  const response = await api.delete(`/events/${eventId}/participants/${participantId}`);
  return response.data;
};

export const removeShiftParticipant = async (shiftId, participantId) => {
  const response = await api.delete(`/shifts/${shiftId}/participants/${participantId}`);
  return response.data;
};

export const getShifts = async (params) => {
  const response = await api.get('/shifts', { params });
  return response.data;
};

export const createShift = async (shiftData) => {
  const response = await api.post('/shifts', shiftData);
  return response.data;
};

export const updateShift = async (shiftId, shiftData) => {
  const response = await api.put(`/shifts/${shiftId}`, shiftData);
  return response.data;
};

export const deleteShift = async (shiftId) => {
  const response = await api.delete(`/shifts/${shiftId}`);
  return response.data;
};

export const joinShift = async (shiftId) => {
  const response = await api.post(`/shifts/${shiftId}/join`);
  return response.data;
};

export const leaveShift = async (shiftId) => {
  const response = await api.post(`/shifts/${shiftId}/leave`);
  return response.data;
};

export const verifyAdminAccess = async () => {
  try {
    const response = await api.get('/auth/verify-token');
    console.log('Token verification response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Token verification error:', error);
    throw error;
  }
};

export default api;
